import React, { Component } from "react";
import { Navigate } from "react-router-dom";

import { withRouter } from "../../../with-router";
import GameService from "../../../services/game";
import AuthService from "../../../services/auth";

import ErrorModal from "../../../components/ErrorModal";
import SurvivorClockBar from "../../../components/SurvivorClockBar";
import { periodAndDeadline } from "../../../utils/pickDeadline";
import "./PrePick.css";
import { visitOver } from "../../../services/visit";

/**
 * Survivor Pool — the pool boards. SUR-460..SUR-466.
 *
 * The entry manager's format, pointed at everybody instead of at one player:
 * one row per ticket, one column per period, the season read across it. A
 * player who has learned to read their own grid can read this one without
 * learning anything new, which is why it borrows PrePick.css wholesale rather
 * than growing a second visual language for the same data.
 *
 * Two differences, both deliberate:
 *
 *   * the ticket icon carries a name — "Bob J." — instead of a slot number
 *     (SUR-462). Slot numbers mean something only to their owner;
 *   * no markers. Nothing on somebody else's row is tappable (SUR-464).
 *
 * Ordering, filtering and the top-ten cut are all the server's (SUR-463). This
 * screen renders what it is handed and derives nothing — the same rule the
 * entry manager follows, and for the same reason: survival is not a thing to
 * work out twice.
 */
class Leaderboard extends Component {
    constructor(props) {
        super(props);

        this.state = {
            page_ready: false,
            redirect: null,
            venue: null,
            player: null,
            promotions: [],
            // SUR-468 — one board on screen at a time, rotating. A venue
            // running Main plus three Second Chance pools cannot show four
            // grids at a readable size, and this screen is read from across a
            // room as often as it is held.
            index: 0,
            error_modal: {show: false, title: "", text: ""}
        };
    }

    /** SUR-467 — /survivor/leaderboard/<slug> names the venue in the URL. */
    slug(){
        return (this.props.router.params && this.props.router.params.slug) || null;
    }

    componentDidMount() {
        // A slug route carries its own venue and needs no session. Without one
        // this screen is part of a player's visit and route state is how it
        // knows whose visit — so no state means there is nothing to show.
        if(!this.slug() && this.props.router.location.state == null){
            this.setState({ redirect: { path: "/home", state: null } });
            return;
        }

        // Black, like the entry manager it sits beside. See PrePick's note:
        // the lobby paints <html> and the pick screen paints <body>, so both
        // are cleared rather than one.
        this.clearInheritedBackground();

        let from_visit = this.props.router.location.state || {};
        this.setState({
            venue: from_visit.venue || null,
            player: from_visit.player || null
        }, () => this.load());

        // The same ~30s tick the entry manager uses (SUR-443). Here it is the
        // board that moves rather than a clock: somebody else's pick landing is
        // the thing worth seeing without a reload.
        this.tick = setInterval(() => this.load(), 30000);

        // SUR-468 — one promotion at a time, next one after ten seconds. Only
        // armed when there is more than one; a single-pool venue should not
        // have its board flicker on a timer that changes nothing.
        this.rotation = setInterval(() => {
            let count = this.state.promotions.length;
            if(count > 1){
                this.setState({index: (this.state.index + 1) % count});
            }
        }, 10000);

        // SUR-469 — a hard reload every 30 minutes. This board runs unattended
        // for days on a screen nobody touches, and a tab left alone that long
        // accumulates whatever the browser and the bundle accumulate. Reloading
        // is the one recovery that needs no one to be standing there.
        this.reload = setTimeout(() => window.location.reload(), 1800000);
    }

    componentWillUnmount() {
        if(this.tick) clearInterval(this.tick);
        if(this.rotation) clearInterval(this.rotation);
        if(this.reload) clearTimeout(this.reload);
        this.clearInheritedBackground();
    }

    clearInheritedBackground(){
        let html = document.getElementsByTagName("html");
        if(html.length > 0) html[0].style.backgroundImage = "";
        document.body.style.backgroundImage = "";
    }

    load(){
        let slug = this.slug();

        // Same trap as PrePick.load: the slugless board is the authenticated
        // one, GameService reads `auth.access` unguarded, and this screen polls
        // every 30s. A session cleared while it is still mounted turns the next
        // tick into an uncaught TypeError. The slug board is public and needs
        // no token, so only guard the authenticated path.
        if(!slug && AuthService.getCurrentAuth() == null){
            if(this.tick) clearInterval(this.tick);
            return;
        }

        // SUR-468 -- the slug board is the one on the wall and has no player
        // to pin. The player's own board names them, so the server can lift
        // their tickets to the top.
        let request = slug
            ? GameService.getSurvivorLeaderboardBySlug(slug)
            : GameService.getSurvivorLeaderboard(
                  null, this.state.player && this.state.player.id
              );

        request.then((data) => {
            let promotions = data.promotions || [];
            this.setState({
                promotions: promotions,
                // Clamped: a promotion ending between refreshes must not leave
                // the rotation pointing past the end of the list.
                index: promotions.length
                    ? Math.min(this.state.index, promotions.length - 1)
                    : 0,
                // The slug route has no session to take a venue from, so the
                // name comes back with the board.
                venue: this.state.venue || data.venue || null,
                page_ready: true
            });
        }, (error) => {
            console.log(error);
            if(visitOver(error, this, {page_ready: true})) return;   // WEB-413
            this.setState({
                page_ready: true,
                error_modal: {
                    show: true,
                    title: "Something went wrong!",
                    text: "We could not load the standings.\nPlease try again."
                }
            });
        });
    }

    back(){
        this.setState({
            redirect: {
                path: "/survivor/prepick",
                state: {
                    venue: this.state.venue,
                    player: this.state.player,
                    reloaded_at: Date.now()
                }
            }
        });
    }

    /**
     * SUR-468 -- one row renderer for both lists.
     *
     * A pinned row and a field row are the same row in a different place, so
     * they are drawn by the same code. Two renderers would eventually disagree
     * about a rank or a cell, and the player would be looking at the one place
     * on the screen where that matters.
     */
    renderRow(row){
        return (
            <tr key={row.ticket_id}
                className={row.is_mine ? "leader-mine" : ""}>
                <td className="prepick-tickethead">
                    {/* Rank travels with the row. A pinned ticket still reads
                        34, because pinning moves it up the screen and not up
                        the standings. Null for a ticket SUR-237 keeps off the
                        board, which has no place to state. */}
                    {row.rank ? (
                        <span className="leader-rank">{row.rank}</span>
                    ) : null}
                    {this.renderTicketIcon(row)}
                    {row.is_mine ? (
                        <span className="leader-mine-tag">You</span>
                    ) : null}
                </td>
                {(row.cells || []).map(cell => (
                    <td key={`${row.ticket_id}-${cell.period_id}`}>
                        {this.renderCell(cell)}
                    </td>
                ))}
            </tr>
        );
    }

    /** SUR-462 — a name where the entry manager puts a slot number. */
    renderTicketIcon(row){
        let dead = row.status && row.status !== "Active";
        return (
            <div className={"prepick-ticket" + (dead ? " prepick-ticket-dead" : "")}>
                <div>
                    <span className="leaderboard-name">{row.label}</span>
                    <span className="prepick-ticket-points">
                        {row.weeks_survived} wk · {row.team_points} pts
                    </span>
                </div>
            </div>
        );
    }

    /** SUR-464 — no markers, so no buttons. The cell states are the entry
     *  manager's and mean the same things. */
    renderCell(cell){
        if(cell.state === "future"){
            return <div className="prepick-cell prepick-cell-future" />;
        }

        let body = null;
        if(cell.state === "no_pick"){
            // SUR-422 — the same stamp the entry manager uses. Two screens
            // drawing one cell state two ways is how a player learns it twice.
            body = (
                <span className="prepick-mask">
                    <span className="prepick-nopick">NO PICK</span>
                </span>
            );
        }else if(cell.logo_url){
            body = <img src={cell.logo_url} alt={cell.choice_name} />;
        }else if(cell.choice_name){
            body = <span className="prepick-nopick-text">{cell.choice_name}</span>;
        }

        let mask = null;
        if(cell.state === "orphaned"){
            mask = (
                <span className="prepick-mask">
                    <span className="prepick-out">OUT</span>
                </span>
            );
        }

        return (
            <div className={`prepick-cell prepick-cell-${cell.state}`}>
                {body}{mask}
            </div>
        );
    }

    renderSection(section){
        let periods = section.periods || [];
        let rows = section.tickets || [];
        // SUR-468 -- the player's own tickets, pinned above the field. The
        // server has already taken them out of `tickets`, so nothing is drawn
        // twice and the two lists never disagree about a ticket's rank.
        let mine = section.mine || [];

        return (
            <div className="prepick-section" key={`lb${section.promo_id}`}>
                {/* SUR-465 — each stacked board is headed by its promo name,
                    which is the only thing telling Main apart from Second
                    Chance. */}
                <h2>{section.promo_name}</h2>

                <div className="prepick-header">
                    {section.picking_closed ? (
                        <span className="prepick-header-value prepick-closed">
                            PICKING IS CLOSED
                        </span>
                    ) : (
                        <span className="prepick-header-value">
                            {periodAndDeadline(
                                section.promo_period_name,
                                section.promo_period_entry_end_time
                            )}
                        </span>
                    )}
                </div>

                {/* SUR-237b — the pool counts come off the full ticket set, not
                    off the ten rows below. "8 of 42 still alive" is only true
                    if 42 counts everybody. */}
                <p className="prepick-guidance">
                    {section.entries_active} of {section.entries_total} still alive
                </p>

                {rows.length === 0 && mine.length === 0 ? (
                    <p className="prepick-guidance">
                        No tickets on the board yet. A ticket earns its place by
                        surviving a week.
                    </p>
                ) : (
                    <div className="prepick-scroll leader-scroll">
                        <table className="prepick-grid">
                            <thead>
                                <tr>
                                    <th className="prepick-cornercell">Ticket</th>
                                    {periods.map(period => (
                                        <th
                                            key={period.id}
                                            className={
                                                period.id === section.promo_period_id
                                                    ? "is-current" : ""
                                            }
                                        >
                                            {period.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mine.map(row => this.renderRow(row))}
                                {mine.length && rows.length ? (
                                    <tr className="leader-divider" aria-hidden="true">
                                        <td colSpan={periods.length + 1}></td>
                                    </tr>
                                ) : null}
                                {rows.map(row => this.renderRow(row))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    render() {
        if(this.state.redirect){
            return <Navigate to={this.state.redirect.path} state={this.state.redirect.state} />;
        }

        return (
            <div className="prepick">
                {/* Banner only. A board showing a fake date is exactly the
                    thing that must not be mistaken for a real one. */}
                <SurvivorClockBar />
                {this.state.venue ? (
                    <p className="prepick-venue">{this.state.venue.name}</p>
                ) : null}
                <h1>Pool Standings</h1>

                {this.state.page_ready && this.state.promotions.length === 0 ? (
                    <div className="prepick-section">
                        <h2>Nothing running right now</h2>
                        <p>There are no Survivor pools open at this venue today.</p>
                    </div>
                ) : null}

                {/* SUR-468 — the one showing now. */}
                {this.state.page_ready && this.state.promotions.length
                    ? this.renderSection(
                        this.state.promotions[
                            Math.min(this.state.index, this.state.promotions.length - 1)
                        ]
                      )
                    : null}

                {/* Which of how many, so a reader knows another is coming and
                    does not take one pool's board for the whole venue's. */}
                {this.state.promotions.length > 1 ? (
                    <p className="leaderboard-rotation">
                        {this.state.promotions.map((section, i) => (
                            <span key={section.promo_id}
                                  className={"leaderboard-dot"
                                             + (i === this.state.index ? " is-on" : "")} />
                        ))}
                    </p>
                ) : null}

                {/* Only when there are tickets to go back to. On a slug route
                    nobody has identified themselves, so there is no "my". */}
                {this.state.player ? (
                    <div className="prepick-footer">
                        <button type="button" className="btn btn-outline-light"
                                onClick={() => this.back()}>
                            ← My Tickets
                        </button>
                    </div>
                ) : null}

                {/* Fatal only — its single action is Return to Lobby. */}
                <ErrorModal show={this.state.error_modal.show}
                            title={this.state.error_modal.title}
                            text={this.state.error_modal.text}
                            restart={this.state.error_modal.restart} />
            </div>
        );
    }
}

export default withRouter(Leaderboard);
